import type { ClientLocationSummaryHighlights } from "api-types/locations/locationsResponses";
import { useMemo, type ReactNode } from "react";
import { formatCount, formatCurrency, formatWholePercent } from "./locationSummaryFormatting";
import styles from "./LocationSummaryView.module.scss";
import StatCard from "./StatCard";

function pluralize(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}

export default function SummaryHighlights({
  highlights,
  totalHomes,
  homesAssignedToLots,
  lotsPossible,
}: {
  highlights: ClientLocationSummaryHighlights;
  totalHomes: number;
  homesAssignedToLots: number;
  lotsPossible: boolean;
}): ReactNode {
  const remainingLots = Math.max(highlights.totalLots - highlights.soldLots, 0);
  // A fresh array each render would re-run DonutChart's color effect and drop any live hover.
  const soldLotsDonut = useMemo(
    () =>
      highlights.totalLots > 0
        ? {
            ariaLabel: `${formatCount(highlights.soldLots)} sold lots and ${formatCount(remainingLots)} remaining lots`,
            segments: [
              { label: "Sold", value: highlights.soldLots, colorVar: "--location-summary-sold-color" },
              { label: "Remaining", value: remainingLots, colorVar: "--location-summary-remaining-color" },
            ],
          }
        : undefined,
    [highlights.soldLots, highlights.totalLots, remainingLots],
  );

  return (
    <div className={styles.highlights}>
      <StatCard
        title="Total Home Plans"
        stat={formatCount(totalHomes)}
        caption={
          lotsPossible
            ? `${formatCount(homesAssignedToLots)} ${pluralize(homesAssignedToLots, "plan")} assigned to ${formatCount(
                highlights.totalLots,
              )} ${pluralize(highlights.totalLots, "lot")}`
            : undefined
        }
      />
      <StatCard title="New Web Leads" stat={formatCount(highlights.newLeads)} caption="Created in last 7 days" />
      <StatCard title="Unique Customers" stat={formatCount(highlights.uniqueCustomers)} caption="Across home plans" />
      <StatCard
        title="Med. Home Price"
        stat={formatCurrency(highlights.medianHomePrice)}
        caption={`${formatCount(totalHomes)} home ${pluralize(totalHomes, "plan")} in summary`}
      />
      {lotsPossible && (
        <StatCard
          title="Lots Sold"
          stat={formatWholePercent(highlights.soldLots, highlights.totalLots)}
          caption={`${formatCount(highlights.soldLots)} of ${formatCount(highlights.totalLots)} ${pluralize(
            highlights.totalLots,
            "lot",
          )} sold`}
          donut={soldLotsDonut}
        />
      )}
    </div>
  );
}
